// exp_PORTFOLIO-58018_file
import { FC } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardStep,
    getCampaignCreationWizardVrProgress,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE, DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { hasVrCampaignErrors, validateVrCampaign, VrCampaignErrors } from 'utils/vrCampaign';

import creationWizardToCampaignsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_to_campaigns_button_click';
import vrPackageCloseButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vr_package/vr_package_close_button_click';
import vrPackageSubmitButtonClick, {
    Preset as PresetAnalytics,
} from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vr_package/vr_package_submit_button_click';
import { ActionBar, Button, ProgressBar, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';

import FormatMoney from 'components/FormatMoney';

import {
    selectVrCampaignAreas,
    selectVrCampaignBudget,
    selectVrCampaignPreset,
    selectVrCampaignRequirements,
    selectVrCampaignResponses,
    selectVrCampaignVacancy,
    setVrCampaignErrors,
    setVrCampaignRequestModalVisible,
} from 'store/models/vrCampaign';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignCreationWizardFlow/CampaignCreationWizardProgress.module.less';

const TrlKeys = {
    toAds: 'CampaignCreationWizard.actionBar.toAds',
    back: 'CampaignCreationWizard.actionBar.back',
    start: 'VrCampaignBuilderPage.actionBar.start',
};

const ERROR_FIELD_MAP: { key: keyof VrCampaignErrors; name: string }[] = [
    { key: 'responses', name: 'responses_count' },
    { key: 'budget', name: 'amount' },
    { key: 'areas', name: 'geo' },
    { key: 'requirements', name: 'hidden_requirements' },
];

export const VrCampaignActions: FC = () => {
    const { vacancyId, vacancyName } = useSelector(selectVrCampaignVacancy);
    const budget = useSelector(selectVrCampaignBudget);
    const responses = useSelector(selectVrCampaignResponses);
    const areas = useSelector(selectVrCampaignAreas);
    const preset = useSelector(selectVrCampaignPreset);
    const requirements = useSelector(selectVrCampaignRequirements);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isXS, isMobile } = useBreakpoint();

    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const isWizardFlowEnabled = Boolean(vacancyId);

    const submitAnalyticsParams = {
        responsesCount: responses,
        amount: budget,
        preset: preset as unknown as PresetAnalytics,
        geoCount: areas.length,
        geoIds: areas.join(','),
        hasHiddenRequirements: requirements.length > 0,
        requirementsLength: requirements.length,
        vacancyId: vacancyId ?? 0,
    };

    const handleStart = () => {
        const errors = validateVrCampaign({ responses, budget, areas, requirements });
        dispatch(setVrCampaignErrors(errors));

        const invalidFields = ERROR_FIELD_MAP.filter(({ key }) => errors[key]).map(({ name }) => name);

        vrPackageSubmitButtonClick({
            ...submitAnalyticsParams,
            error: invalidFields.length > 0 ? invalidFields.join(',') : null,
        });

        if (hasVrCampaignErrors(errors)) {
            return;
        }

        dispatch(setVrCampaignRequestModalVisible(true));
    };

    const handleToAdsClick = () => {
        creationWizardToCampaignsButtonClick();
        if (isWizardFlowEnabled) {
            openBackAlert(() => {
                vrPackageCloseButtonClick();
                dispatch(push(DASHBOARD_PAGE_ROUTE));
            });
        } else {
            vrPackageCloseButtonClick();
            dispatch(push(DASHBOARD_PAGE_ROUTE));
        }
    };

    const handleBackClick = () => {
        if (isWizardFlowEnabled) {
            openBackAlert(() => {
                vrPackageCloseButtonClick();
                goToWizardStep(CampaignCreationWizardStep.PromotionType, {
                    vacancyId: vacancyId ?? null,
                    vacancyName: vacancyName ?? undefined,
                    productType: CampaignProductType.Vr,
                });
            });
        } else {
            vrPackageCloseButtonClick();
            dispatch(push(CREATE_MENU_PAGE_ROUTE, { saveMainMenuData: true }));
        }
    };

    const primaryActions = (
        <>
            <Button mode="secondary" style="accent" onClick={handleBackClick} data-qa="vr-create-back">
                {trls(TrlKeys.back)}
            </Button>
            <Button
                mode="primary"
                style="accent"
                onClick={handleStart}
                data-qa="vr-create-start"
                postfix={<FormatMoney groupSeparator={NON_BREAKING_SPACE}>{budget}</FormatMoney>}
                spaceBetween={false}
            >
                {trls(TrlKeys.start)}
            </Button>
        </>
    );

    const secondaryActions = !isMobile && (
        <Button mode="secondary" style="accent" onClick={handleToAdsClick} data-qa="vr-create-to-ads">
            {trls(TrlKeys.toAds)}
        </Button>
    );

    if (isWizardFlowEnabled) {
        const progressConfig = getCampaignCreationWizardVrProgress();
        const progress = (
            <ProgressBar
                progress={progressConfig.progress}
                segments={progressConfig.segments}
                data-qa="campaign-creation-wizard-progress"
            />
        );

        return (
            <>
                <ActionBar
                    type={isXS ? 'vertical' : 'auto'}
                    isSticky
                    showProgress={progress}
                    additionalContent={isMobile ? <div className={styles.progress}>{progress}</div> : null}
                    primaryActions={primaryActions}
                    secondaryActions={secondaryActions}
                />
                {backAlert}
            </>
        );
    }

    return (
        <ActionBar
            type={isXS ? 'vertical' : 'auto'}
            isSticky
            showDivider="always"
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
        />
    );
};
